import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Wand2, ImagePlus, ArrowLeft, Star, Crown, Trash2, Pencil, Flame, Lock, Images, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  "Fantasy", "Sci-Fi", "Cyberpunk", "Anime", "Harem", "RPG", "Isekai", "Romance",
  "Adventure", "Mystery", "Superhero", "Villain", "Historical", "Horror", "Comedy",
  "Vampire", "Werewolf", "Mafia", "Yandere", "Tsundere", "Monster Girl", "Demon", "Angel",
  "Idol / VTuber", "Mecha", "Gaming", "Music", "Assistant / Helper", "Helpers",
  "Educational", "Slice of Life", "Paranormal",
];

const GENDERS = ["Female", "Male", "Non-binary"];

const STYLE_SAMPLES = {
  realistic: "https://static.prod-images.emergentagent.com/jobs/b1afe473-e19a-40bd-8e62-7cded4849b5d/images/bb50abfd3b9246d48e0fc1e6035c7eb7d823abd8079a866516c490b4e4797975.jpeg",
  anime: "https://static.prod-images.emergentagent.com/jobs/b1afe473-e19a-40bd-8e62-7cded4849b5d/images/8ad4ad55b07d3a7db4559de2960c1dc66485bb7aef575159d41ee395b8226302.jpeg",
};

const TONES = [
  "Verspielt & humorvoll", "Romantisch", "Dramatisch", "Düster & ernst",
  "Episch & heroisch", "Geheimnisvoll", "Wholesome & sanft", "Spannend & actionreich",
];

export default function CreateCharacter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdult = Boolean(user?.is_owner || (user?.age != null && user.age >= 18));
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [form, setForm] = useState({
    name: "",
    title: "",
    category: "Fantasy",
    gender: "Female",
    tagsText: "",
    greeting: "",
    persona: "",
    scenario: "",
    tone: "",
    avatar_url: "",
    gallery: [],
    is_public: true,
    is_vip: false,
    mature: false,
  });
  const [genPrompt, setGenPrompt] = useState("");
  const [avatarStyle, setAvatarStyle] = useState("realistic");
  const [generating, setGenerating] = useState(false);
  const [galleryPrompt, setGalleryPrompt] = useState("");
  const [genGallery, setGenGallery] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!editId) return;
    api
      .get(`/characters/${editId}`)
      .then((res) => {
        const c = res.data;
        setForm({
          name: c.name || "",
          title: c.title || "",
          category: c.category || "Fantasy",
          gender: c.gender || "Female",
          tagsText: (c.tags || []).join(", "),
          greeting: c.greeting || "",
          persona: c.persona || "",
          scenario: c.scenario || "",
          tone: c.tone || "",
          avatar_url: c.avatar_url || "",
          gallery: c.gallery || [],
          is_public: c.is_public !== false,
          is_vip: Boolean(c.is_vip),
          mature: Boolean(c.mature),
        });
      })
      .catch(() => {
        toast.error("Charakter nicht gefunden");
        navigate("/profile");
      });
  }, [editId, navigate]);

  const generateAvatar = async () => {
    const prompt = genPrompt.trim() || `${form.name} ${form.title} ${form.category}`.trim();
    if (!prompt) {
      toast.error("Add a name/title or describe the look first");
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post("/characters/generate-avatar", { prompt, style: avatarStyle, gender: form.gender });
      set("avatar_url", res.data.avatar_url);
      toast.success("Avatar generated!");
    } catch (e) {
      toast.error("Avatar generation failed. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  const addGalleryImage = async () => {
    if (form.gallery.length >= 6) {
      toast.error("Maximal 6 Galeriebilder");
      return;
    }
    const prompt = galleryPrompt.trim() || `${form.name} ${form.title} ${form.category}`.trim();
    if (!prompt) {
      toast.error("Beschreibe zuerst das Bild oder gib einen Namen ein");
      return;
    }
    setGenGallery(true);
    try {
      const res = await api.post("/characters/generate-avatar", { prompt, style: avatarStyle, gender: form.gender });
      set("gallery", [...form.gallery, res.data.avatar_url]);
      setGalleryPrompt("");
      toast.success("Galeriebild hinzugefügt!");
    } catch (e) {
      toast.error("Bild-Erstellung fehlgeschlagen");
    } finally {
      setGenGallery(false);
    }
  };

  const removeGalleryImage = (idx) => {
    set("gallery", form.gallery.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!form.name || !form.title || !form.greeting || !form.persona) {
      toast.error("Please fill name, title, greeting and personality");
      return;
    }
    setSaving(true);
    try {
      const tags = form.tagsText.split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        name: form.name,
        title: form.title,
        category: form.category,
        gender: form.gender,
        tags,
        greeting: form.greeting,
        persona: form.persona,
        scenario: form.scenario,
        tone: form.tone,
        gallery: form.gallery,
        is_public: form.is_public,
        is_vip: form.is_vip,
        mature: form.mature,
        avatar_url:
          form.avatar_url ||
          "https://images.unsplash.com/photo-1559828801-04565cd31e27?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
      };
      if (isEdit) {
        await api.put(`/characters/${editId}`, payload);
        toast.success(`${form.name} aktualisiert!`);
        navigate("/profile");
      } else {
        const res = await api.post("/characters", payload);
        toast.success(`${res.data.name} created!`);
        const chat = await api.post("/chats", { character_id: res.data.id });
        navigate(`/chat/${chat.data.session_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save character");
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`${form.name} wirklich löschen? Alle zugehörigen Chats gehen verloren.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/characters/${editId}`);
      toast.success("Charakter gelöscht");
      navigate("/profile");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
      setDeleting(false);
    }
  };

  const inputCls =
    "w-full bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all";

  const previewTags = form.tagsText.split(",").map((t) => t.trim()).filter(Boolean).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button
          data-testid="create-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white flex items-center gap-2">
              {isEdit ? <Pencil className="w-6 h-6 text-fuchsia-400" /> : <Wand2 className="w-6 h-6 text-fuchsia-400" />}
              {isEdit ? "Charakter bearbeiten" : "Create a Character"}
            </h1>
            <p className="text-slate-400 text-sm mt-1 mb-7">
              {isEdit ? "Passe deinen Charakter an oder lösche ihn." : "Design a personality and bring it to life with AI."}
            </p>

            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Name *</label>
                  <input
                    data-testid="create-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="e.g. Luna Starweaver"
                    className={`${inputCls} mt-2`}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Category</label>
                  <select
                    data-testid="create-category"
                    value={form.category}
                    onChange={(e) => set("category", e.target.value)}
                    className={`${inputCls} mt-2`}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c} className="bg-[#12141D]">
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Geschlecht</label>
                <div className="flex gap-2 mt-2" data-testid="create-gender">
                  {GENDERS.map((g) => (
                    <button
                      key={g}
                      type="button"
                      data-testid={`create-gender-${g.toLowerCase()}`}
                      onClick={() => set("gender", g)}
                      className={`flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold border transition-all ${
                        form.gender === g
                          ? "bg-fuchsia-500/15 border-fuchsia-400/40 text-white"
                          : "bg-[#0d0e15] border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                      }`}
                    >
                      {g === "Female" ? "Weiblich" : g === "Male" ? "Männlich" : "Divers"}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tagline *</label>
                <input
                  data-testid="create-title"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. Mystic Guardian of the Moon Temple"
                  className={`${inputCls} mt-2`}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tags (comma separated)</label>
                <input
                  data-testid="create-tags"
                  value={form.tagsText}
                  onChange={(e) => set("tagsText", e.target.value)}
                  placeholder="Magic, Mysterious, Kind"
                  className={`${inputCls} mt-2`}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Personality & Background *</label>
                <textarea
                  data-testid="create-persona"
                  rows={4}
                  value={form.persona}
                  onChange={(e) => set("persona", e.target.value)}
                  placeholder="Describe who they are, how they speak, their backstory and quirks..."
                  className={`${inputCls} mt-2 resize-none`}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">First Greeting *</label>
                <textarea
                  data-testid="create-greeting"
                  rows={3}
                  value={form.greeting}
                  onChange={(e) => set("greeting", e.target.value)}
                  placeholder="*smiles warmly* Hello traveler, what brings you here?"
                  className={`${inputCls} mt-2 resize-none`}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Szenario / Welt (optional)</label>
                <textarea
                  data-testid="create-scenario"
                  rows={2}
                  value={form.scenario}
                  onChange={(e) => set("scenario", e.target.value)}
                  placeholder="z.B. Eine mittelalterliche RPG-Welt voller Quests und Monster..."
                  className={`${inputCls} mt-2 resize-none`}
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Stimmung / Ton (optional)</label>
                <Select value={form.tone} onValueChange={(v) => set("tone", v)}>
                  <SelectTrigger data-testid="create-tone" className={`${inputCls} mt-2 h-auto`}>
                    <SelectValue placeholder="Ton wählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#12141D] border-white/10 text-white">
                    {TONES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-[#12141D] border border-white/10 rounded-2xl p-4">
                <label className="text-xs font-semibold text-fuchsia-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> AI Avatar
                </label>
                <div className="grid grid-cols-2 gap-3 mt-3" data-testid="avatar-style-toggle">
                  {[
                    { k: "realistic", label: "Realistisch" },
                    { k: "anime", label: "Anime" },
                  ].map((s) => (
                    <button
                      key={s.k}
                      type="button"
                      data-testid={`avatar-style-${s.k}`}
                      onClick={() => setAvatarStyle(s.k)}
                      className={`group relative rounded-xl overflow-hidden border-2 transition-all text-left ${
                        avatarStyle === s.k
                          ? "border-fuchsia-400 ring-2 ring-fuchsia-500/30"
                          : "border-white/10 hover:border-white/25"
                      }`}
                    >
                      <div className="aspect-[4/3] overflow-hidden bg-[#0d0e15]">
                        <img
                          src={STYLE_SAMPLES[s.k]}
                          alt={`${s.label} Beispiel`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      <span className="absolute bottom-2 left-2 text-sm font-semibold text-white flex items-center gap-1.5">
                        {avatarStyle === s.k && <Sparkles className="w-3.5 h-3.5 text-fuchsia-300" />}
                        {s.label}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">
                  Beispiel-Vorschau &middot; Stil: {avatarStyle === "anime" ? "Anime" : "Realistisch"} &middot; Geschlecht: {form.gender === "Female" ? "Weiblich" : form.gender === "Male" ? "Männlich" : "Divers"}
                </p>
                <div className="flex gap-2 mt-3">
                  <input
                    data-testid="create-avatar-prompt"
                    value={genPrompt}
                    onChange={(e) => setGenPrompt(e.target.value)}
                    placeholder="Describe the look (or leave blank to use name)"
                    className={inputCls}
                  />
                  <button
                    data-testid="generate-avatar-btn"
                    onClick={generateAvatar}
                    disabled={generating}
                    className="flex-shrink-0 flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium rounded-xl px-4 active:scale-95 transition-all disabled:opacity-60"
                  >
                    <ImagePlus className="w-4 h-4" /> {generating ? "..." : form.avatar_url ? "Neu generieren" : "Generate"}
                  </button>
                </div>
              </div>

              <div className="bg-[#12141D] border border-white/10 rounded-2xl p-4">
                <label className="text-xs font-semibold text-fuchsia-300 uppercase tracking-wider flex items-center gap-1.5">
                  <Images className="w-3.5 h-3.5" /> Galerie (Beispielbilder, max. 6)
                </label>
                {form.gallery.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3" data-testid="gallery-grid">
                    {form.gallery.map((g, i) => (
                      <div key={i} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10">
                        <img src={g} alt={`gallery ${i}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          data-testid={`gallery-remove-${i}`}
                          onClick={() => removeGalleryImage(i)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all hover:bg-rose-500"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <input
                    data-testid="gallery-prompt"
                    value={galleryPrompt}
                    onChange={(e) => setGalleryPrompt(e.target.value)}
                    placeholder="Beschreibe ein Beispielbild (KI)"
                    className={inputCls}
                  />
                  <button
                    type="button"
                    data-testid="gallery-add-btn"
                    onClick={addGalleryImage}
                    disabled={genGallery || form.gallery.length >= 6}
                    className="flex-shrink-0 flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/10 text-white font-medium rounded-xl px-4 active:scale-95 transition-all disabled:opacity-60"
                  >
                    <ImagePlus className="w-4 h-4" /> {genGallery ? "..." : "Hinzufügen"}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="create-toggle-public"
                  onClick={() => set("is_public", !form.is_public)}
                  className={`flex items-center justify-between rounded-xl px-4 py-3 border text-left transition-all ${
                    form.is_public
                      ? "bg-emerald-500/10 border-emerald-400/40"
                      : "bg-[#0d0e15] border-white/10"
                  }`}
                >
                  <div>
                    <p className="text-sm font-semibold text-white flex items-center gap-1">
                      {form.is_public ? "Öffentlich" : "Privat"}
                      {!form.is_public && <Lock className="w-3.5 h-3.5 text-slate-400" />}
                    </p>
                    <p className="text-xs text-slate-400">
                      {form.is_public ? "Für alle sichtbar" : "Nur du siehst ihn"}
                    </p>
                  </div>
                  <span
                    className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-all ${
                      form.is_public ? "bg-emerald-500 justify-end" : "bg-slate-600 justify-start"
                    }`}
                  >
                    <span className="w-5 h-5 rounded-full bg-white" />
                  </span>
                </button>

                <button
                  type="button"
                  data-testid="create-toggle-mature"
                  onClick={() => {
                    if (!isAdult) {
                      toast.error("Nur für Nutzer ab 18. Bitte setze dein Alter (18+) im Profil.");
                      return;
                    }
                    set("mature", !form.mature);
                  }}
                  className={`flex items-center justify-between rounded-xl px-4 py-3 border text-left transition-all ${
                    !isAdult
                      ? "bg-[#0d0e15] border-white/10 opacity-60"
                      : form.mature
                      ? "bg-rose-500/10 border-rose-400/40"
                      : "bg-[#0d0e15] border-white/10"
                  }`}
                >
                  <div>
                    <p className="text-sm font-semibold text-white flex items-center gap-1">
                      <Flame className="w-3.5 h-3.5 text-rose-300" /> 18+ / Reif
                    </p>
                    <p className="text-xs text-slate-400">
                      {isAdult ? "Intensivere, reifere Themen" : "Alter 18+ im Profil nötig"}
                    </p>
                  </div>
                  <span
                    className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-all ${
                      form.mature && isAdult ? "bg-rose-500 justify-end" : "bg-slate-600 justify-start"
                    }`}
                  >
                    <span className="w-5 h-5 rounded-full bg-white" />
                  </span>
                </button>

                <button
                  type="button"
                  data-testid="create-toggle-vip"
                  onClick={() => set("is_vip", !form.is_vip)}
                  className={`flex items-center justify-between rounded-xl px-4 py-3 border text-left transition-all ${
                    form.is_vip
                      ? "bg-gradient-to-r from-amber-500/15 to-fuchsia-500/15 border-fuchsia-400/40"
                      : "bg-[#0d0e15] border-white/10"
                  }`}
                >
                  <div>
                    <p className="text-sm font-semibold text-white flex items-center gap-1">
                      <Crown className="w-3.5 h-3.5 text-amber-300" /> VIP-Charakter
                    </p>
                    <p className="text-xs text-slate-400">Mehr RPG-Freiheit, nur für VIP</p>
                  </div>
                  <span
                    className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-all ${
                      form.is_vip ? "bg-fuchsia-500 justify-end" : "bg-slate-600 justify-start"
                    }`}
                  >
                    <span className="w-5 h-5 rounded-full bg-white" />
                  </span>
                </button>
              </div>

              <button
                data-testid="create-save-btn"
                onClick={save}
                disabled={saving}
                className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold rounded-xl py-3.5 shadow-lg shadow-violet-500/20 active:scale-95 transition-all disabled:opacity-60"
              >
                {saving ? "Speichern..." : isEdit ? "Änderungen speichern" : "Create & Start Chatting"}
              </button>

              {isEdit && (
                <button
                  data-testid="character-delete-btn"
                  onClick={remove}
                  disabled={deleting}
                  className="w-full flex items-center justify-center gap-2 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-400/30 text-rose-300 font-medium rounded-xl py-3 active:scale-95 transition-all disabled:opacity-60"
                >
                  <Trash2 className="w-4 h-4" /> {deleting ? "Löschen..." : "Charakter löschen"}
                </button>
              )}
            </div>
          </div>

          {/* Live Preview */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Live Preview</p>
              <motion.div
                layout
                className="rounded-2xl overflow-hidden bg-[#131520] border border-white/5 shadow-xl"
              >
                <div className="relative aspect-[3/4] bg-[#0d0e15]">
                  {form.avatar_url ? (
                    <img src={form.avatar_url} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-600 gap-2">
                      {generating ? (
                        <>
                          <Sparkles className="w-8 h-8 animate-pulse text-fuchsia-400" />
                          <span className="text-sm">Conjuring avatar...</span>
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-8 h-8" />
                          <span className="text-sm">Avatar preview</span>
                        </>
                      )}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b0c12] via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-black/50 backdrop-blur-md border border-white/10 text-fuchsia-300">
                    {form.category}
                  </span>
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <h3 className="font-display font-bold text-lg text-white">{form.name || "Your Character"}</h3>
                    <p className="text-xs text-slate-300 line-clamp-1">{form.title || "Their tagline appears here"}</p>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-1.5 min-h-[26px]">
                    {previewTags.length ? (
                      previewTags.map((t) => (
                        <span key={t} className="px-2.5 py-1 rounded-full text-[10px] font-medium bg-white/5 text-slate-400 border border-white/5">
                          {t}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-slate-600">Tags preview</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-3 line-clamp-2 italic">
                    {form.greeting || "The first greeting will show here..."}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
